import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Package, CurrencyDollar, TrendUp, CursorClick, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function AdvertiserDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', type: 'CPA', payout: '', category: 'general', url: '' });

  const fetchData = () => {
    api.get('/advertiser/dashboard').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const createOffer = async (e) => {
    e.preventDefault();
    try {
      await api.post('/advertiser/offers', { ...form, payout: parseFloat(form.payout) || 0, geo_targets: ['US', 'UK'] });
      toast.success('Offer created successfully');
      setDialogOpen(false);
      setForm({ title: '', description: '', type: 'CPA', payout: '', category: 'general', url: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed to create offer');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-zinc-400">Failed to load dashboard.</p>;

  const { kpis, offers } = data;

  return (
    <div className="space-y-6" data-testid="advertiser-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Advertiser Dashboard</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage your offers and campaigns</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-offer-btn" className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
              <Plus size={16} /> New Offer
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            <DialogHeader>
              <DialogTitle className="font-heading text-white">Create New Offer</DialogTitle>
            </DialogHeader>
            <form onSubmit={createOffer} className="space-y-4">
              <div>
                <Label className="text-zinc-300">Title</Label>
                <Input data-testid="offer-title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
              </div>
              <div>
                <Label className="text-zinc-300">Description</Label>
                <Input data-testid="offer-description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Type</Label>
                  <select data-testid="offer-type" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-100 text-sm">
                    <option value="CPA">CPA</option>
                    <option value="CPL">CPL</option>
                    <option value="RevShare">RevShare</option>
                    <option value="Subscription">Subscription</option>
                  </select>
                </div>
                <div>
                  <Label className="text-zinc-300">Payout ($)</Label>
                  <Input data-testid="offer-payout" type="number" step="0.01" value={form.payout} onChange={e => setForm(p => ({ ...p, payout: e.target.value }))} required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">Category</Label>
                <select data-testid="offer-category" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-100 text-sm">
                  {['general', 'E-Commerce', 'Finance', 'Gaming', 'SaaS', 'Health', 'Education', 'Travel'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-zinc-300">URL</Label>
                <Input data-testid="offer-url" value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://..." className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
              </div>
              <Button data-testid="submit-offer" type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">Create Offer</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Active Offers', value: kpis.active_offers || 0, icon: Package, color: 'text-blue-400' },
          { label: 'Total Spend', value: `$${(kpis.total_spend || 0).toLocaleString()}`, icon: CurrencyDollar, color: 'text-amber-400' },
          { label: 'Conversions', value: (kpis.total_conversions || 0).toLocaleString(), icon: TrendUp, color: 'text-emerald-400' },
          { label: 'Total Clicks', value: (kpis.total_clicks || 0).toLocaleString(), icon: CursorClick, color: 'text-blue-400' },
          { label: 'Avg CPA', value: `$${kpis.avg_cpa || 0}`, icon: CurrencyDollar, color: 'text-emerald-400' },
        ].map((item, i) => (
          <Card key={i} className={`bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all animate-fade-in stagger-${i+1}`}>
            <CardContent className="p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{item.label}</p>
              <p className={`text-xl font-bold tracking-tight mt-1 font-heading ${item.color}`}>{item.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg text-white">My Offers</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Offer</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Type</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Payout</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Clicks</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Conv.</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(offers || []).map((o, i) => (
                <TableRow key={i} className="border-zinc-800/50" data-testid={`offer-row-${i}`}>
                  <TableCell className="text-zinc-200 font-medium">{o.title}</TableCell>
                  <TableCell><Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[10px]">{o.type}</Badge></TableCell>
                  <TableCell className="text-emerald-400 font-mono">${o.payout}</TableCell>
                  <TableCell className="text-zinc-300">{(o.total_clicks || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-zinc-300">{o.total_conversions || 0}</TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] ${o.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-zinc-700/30 text-zinc-400'}`}>
                      {o.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {(!offers || offers.length === 0) && (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">No offers yet. Create your first offer.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
